import React, { useEffect, useMemo, useState } from 'react'
import { Empty, Spin } from 'antd'
import { RouteComponentProps } from 'react-router'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import Button from '@/components/button/button'
import Tab, { TabItem } from '@/components/tab/tab'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { Column, Expaned, Row } from '@/components/layout/layout'
import Auth from '@/views/main/components/auth/auth'
import $msg from '@/utils/message'
import { Modal as SgModal } from '@/components/dialog/modal'
import { layerManage, showLayer } from '@/utils/layer'
import { IMember } from '@/model/types/member'
import {
    blacklistMember,
    deleteMember,
    getMemberDetail,
    getMemberGroupList,
    getMemberLevelList,
    unblacklistMember,
} from '../api'
import { MEMBER_LIST } from '../router'
import { canDeleteMember, getMemberBalanceValue } from '../member-list/member-list-utils'
import ChangeLevelModal from './change-level-modal'
import ChangePointsModal from './change-points-modal'
import EditMemberModal from './edit-member-modal'
import RechargeMemberModal from './recharge-member-modal'
import MemberBalanceRefundModal from './member-balance-refund-modal'
import BalanceChange from './records/balance-change'
import ChainCoinRecord from './records/chain-coin-record'
import PayRecord from './records/pay-record'
import PointsRecord from './records/points-record'
import SaleRecord from './records/sale-record'
import MemberPageHeader from '../member-page-header'

import './member-detail.scss'

interface Props extends Partial<RouteComponentProps> {
    memberId?: number
    drawer?: boolean
    onSuccess?: () => void
}

const sourceMap: { [key: number]: string } = {
    0: '甜杏小程序',
    1: '零售通手动添加',
    2: '老零售会员',
    3: '表格导入',
}

const formatDate = (date?: string | null) => (date ? dayjs(date).format('YYYY-MM-DD') : '-')
const formatTime = (date?: string | null) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-')
const formatAmount = (amount?: string | number) =>
    amount === undefined || amount === null || amount === '' ? 0 : amount

const getMemberName = (member: IMember) => member.name || member.user?.nickname || '-'

const MemberDetail: React.FC<Props> = props => {
    const [editVisible, setEditVisible] = useState(false)
    const [pointsVisible, setPointsVisible] = useState(false)
    const [levelVisible, setLevelVisible] = useState(false)
    const [rechargeVisible, setRechargeVisible] = useState(false)
    const [refundVisible, setRefundVisible] = useState(false)
    const [recordTab, setRecordTab] = useState(0)
    const locationSearch = props.location ? props.location.search : ''

    const routeMemberId = useMemo(() => {
        const params = new URLSearchParams(locationSearch)

        return Number(params.get('id'))
    }, [locationSearch])
    const memberId = props.memberId || routeMemberId
    const recordTabs: TabItem[] = useMemo(
        () => [
            { id: 0, name: '消费记录' },
            { id: 1, name: '余额变动' },
            { id: 2, name: '积分变动' },
            { id: 3, name: '付费记录' },
            { id: 4, name: '连锁币变动' },
        ],
        [],
    )

    const detailReq = useRequest(() => getMemberDetail(memberId), {
        manual: true,
        onError: e => $msg('error', e.message || '获取会员详情失败'),
    })

    const groupsReq = useRequest(getMemberGroupList, {
        onError: e => $msg('error', e.message || '获取会员分组失败'),
    })
    const levelsReq = useRequest(getMemberLevelList, {
        onError: e => $msg('error', e.message || '获取会员等级失败'),
    })
    const blacklistReq = useRequest(blacklistMember, {
        manual: true,
        onSuccess: () => {
            $msg('success', '已拉黑会员')
            detailReq.refresh()
        },
        onError: e => $msg('error', e.message || '拉黑会员失败'),
    })
    const unblacklistReq = useRequest(unblacklistMember, {
        manual: true,
        onSuccess: () => {
            $msg('success', '已解除拉黑')
            detailReq.refresh()
        },
        onError: e => $msg('error', e.message || '解除拉黑失败'),
    })

    const member = detailReq.data

    useEffect(() => {
        if (!memberId) return

        detailReq.run()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberId])

    const refreshDetail = () => {
        setEditVisible(false)
        setPointsVisible(false)
        setLevelVisible(false)
        setRechargeVisible(false)
        setRefundVisible(false)
        props.onSuccess && props.onSuccess()
        detailReq.refresh()
    }
    const closeDetail = () => {
        if (props.drawer) {
            layerManage.pop()

            return
        }

        props.history && props.history.replace(MEMBER_LIST)
    }

    const onDeleteMember = (record: IMember) => {
        showLayer(
            <SgModal
                headerStyles={{ width: '440px' }}
                isShow
                type="danger"
                style={{ height: '156px' }}
                onClose={() => layerManage.pop()}
                onCancel={() => layerManage.pop()}
                onConfirm={() => {
                    layerManage.pop()
                    deleteMember(record.id)
                        .then(() => {
                            $msg('success', '删除会员成功')
                            if (props.drawer) {
                                props.onSuccess && props.onSuccess()
                                layerManage.pop()
                            } else {
                                props.history && props.history.replace(MEMBER_LIST)
                            }
                        })
                        .catch(e => $msg('error', e.message || '删除会员失败'))
                }}
            >
                <div>
                    <div data-font-size-20 data-font-weight-600 data-color-000>
                        确认删除手工会员
                        <span data-color-ff4343 data-margin-h-6>
                            {getMemberName(record)}
                        </span>
                        吗？
                    </div>
                </div>
            </SgModal>,
        )
    }
    const onToggleBlacklist = (record: IMember) => {
        const isBlacklisted = !!record.is_miniapp_blacklisted
        showLayer(
            <SgModal
                headerStyles={{ width: '440px' }}
                isShow
                type="danger"
                style={{ height: '156px' }}
                okText={isBlacklisted ? '解除拉黑' : '确认拉黑'}
                onClose={() => layerManage.pop()}
                onCancel={() => layerManage.pop()}
                onConfirm={() => {
                    layerManage.pop()
                    if (isBlacklisted) {
                        unblacklistReq.run({ member_id: record.id })
                    } else {
                        blacklistReq.run({
                            member_id: record.id,
                            miniapp_blacklist_level: record.miniapp_blacklist_level || 1,
                        })
                    }
                }}
            >
                <div>
                    <div data-font-size-20 data-font-weight-600 data-color-000>
                        {isBlacklisted ? '确认解除拉黑？' : '确认拉黑该会员？'}
                    </div>
                    <div data-font-size-14 data-color-999 data-margin-top-8>
                        {isBlacklisted
                            ? '解除后，该会员将恢复甜杏小程序下单权限。'
                            : '拉黑后，该会员将按会员黑名单设置限制小程序下单。'}
                    </div>
                </div>
            </SgModal>,
        )
    }

    if (!memberId) {
        const emptyBody = (
            <Expaned className="member-detail__empty">
                <Empty description="缺少会员ID" />
            </Expaned>
        )

        return props.drawer ? (
            <div className="member-detail-dialog-layer">
                <BaseDialog isShow title="会员详情" width={1000} onClose={closeDetail}>
                    <Column className="member-detail member-detail--dialog">{emptyBody}</Column>
                </BaseDialog>
            </div>
        ) : (
            <Column className="member-detail">
                <MemberPageHeader title="会员详情" onBack={closeDetail} backText="返回列表" />
                {emptyBody}
            </Column>
        )
    }

    const detailItems = member
        ? [
              { label: '会员分组', value: member.group?.name || '-' },
              { label: '会员等级', value: member.level?.name || '-' },
              { label: '生日', value: formatDate(member.birthday) },
              { label: '来源', value: member.source === undefined ? '-' : sourceMap[member.source] || '-' },
              {
                  label: '累计充值',
                  value: formatAmount(member.accumulated_recharge || member.total_recharge_amount),
              },
              {
                  label: '累计消费',
                  value: formatAmount(member.accumulated_consume || member.total_consume_amount),
              },
              {
                  label: '消费次数',
                  value: member.accumulated_consume_count || member.total_consume_times || 0,
              },
              {
                  label: '付费会员',
                  value: member.is_svip ? `有效至 ${formatTime(member.svip_expire_time)}` : '否',
              },
              { label: '邀请人', value: member.inviter?.name || '-' },
              { label: '所属门店', value: member.shop?.name || '-' },
              { label: '注册时间', value: formatTime(member.create_time) },
              { label: '升级时间', value: formatTime(member.date_leveled_up) },
          ]
        : []

    const detailBody = (
        <Expaned className="member-detail__body">
            <Spin spinning={detailReq.loading}>
                {member ? (
                    <>
                        <div className="member-detail__header">
                            <Row className="member-detail__profile" data-align-items-center>
                                <div className="member-detail__avatar">{getMemberName(member).slice(0, 1)}</div>
                                <div>
                                    <div className="member-detail__name">{getMemberName(member)}</div>
                                    <div className="member-detail__sub">
                                        {member.phone || '-'} / 卡号：{member.card_code || '-'}
                                    </div>
                                </div>
                                <Row className="member-detail__actions">
                                    <Auth perssion={19} onClick={() => setEditVisible(true)}>
                                        <Button types="primary">编辑资料</Button>
                                    </Auth>
                                    <Auth perssion={50} onClick={() => setPointsVisible(true)}>
                                        <Button types="primary">调整积分</Button>
                                    </Auth>
                                    <Auth perssion={34} onClick={() => setRechargeVisible(true)}>
                                        <Button types="primary">手动充值</Button>
                                    </Auth>
                                    {member.status !== -1 &&
                                    (Number(member.balance || 0) > 0 || Number(member.bonus || 0) > 0) ? (
                                        <Auth perssion={51} onClick={() => setRefundVisible(true)}>
                                            <Button types="error">余额退款</Button>
                                        </Auth>
                                    ) : null}
                                    <Auth perssion={47} onClick={() => setLevelVisible(true)}>
                                        <Button types="primary">调整等级</Button>
                                    </Auth>
                                    <Auth perssion={20} onClick={() => onToggleBlacklist(member)}>
                                        <Button types={member.is_miniapp_blacklisted ? 'primary' : 'error'}>
                                            {member.is_miniapp_blacklisted ? '解除拉黑' : '拉黑会员'}
                                        </Button>
                                    </Auth>
                                    {canDeleteMember(member) ? (
                                        <Auth perssion={28} onClick={() => onDeleteMember(member)}>
                                            <Button types="error">删除会员</Button>
                                        </Auth>
                                    ) : null}
                                </Row>
                            </Row>
                            <div className="member-detail__stats">
                                <div>
                                    <span>余额</span>
                                    <strong>{getMemberBalanceValue(member)}</strong>
                                    {Number(member.bonus || 0) > 0 ? (
                                        <p className="member-detail__stats-note">
                                            含赠送金 {formatAmount(member.bonus)} 元
                                        </p>
                                    ) : null}
                                </div>
                                <div>
                                    <span>赠送金</span>
                                    <strong>{formatAmount(member.bonus)}</strong>
                                </div>
                                <div>
                                    <span>积分</span>
                                    <strong>{member.points || 0}</strong>
                                </div>
                                <div>
                                    <span>成长值</span>
                                    <strong>{member.experience || 0}</strong>
                                </div>
                            </div>
                            <div className="member-detail__info-grid">
                                {detailItems.map(item => (
                                    <div className="member-detail__info-item" key={item.label}>
                                        <span className="member-detail__info-label">{item.label}：</span>
                                        <span className="member-detail__info-value">{item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="member-detail__records">
                            <Tab tabList={recordTabs} activeId={recordTab} onClick={item => setRecordTab(item.id)} />
                            <div className="member-detail__record-content">
                                {recordTab === 0 ? <SaleRecord memberId={member.id} /> : null}
                                {recordTab === 1 ? <BalanceChange memberId={member.id} /> : null}
                                {recordTab === 2 ? <PointsRecord memberId={member.id} /> : null}
                                {recordTab === 3 ? <PayRecord memberId={member.id} /> : null}
                                {recordTab === 4 ? <ChainCoinRecord memberId={member.id} /> : null}
                            </div>
                        </div>
                        <EditMemberModal
                            visible={editVisible}
                            member={member}
                            groups={groupsReq.data || []}
                            onCancel={() => setEditVisible(false)}
                            onSuccess={refreshDetail}
                        />
                        <ChangePointsModal
                            visible={pointsVisible}
                            memberId={member.id}
                            currentPoints={member.points || 0}
                            onCancel={() => setPointsVisible(false)}
                            onSuccess={refreshDetail}
                        />
                        <ChangeLevelModal
                            visible={levelVisible}
                            member={member}
                            levels={levelsReq.data || []}
                            onCancel={() => setLevelVisible(false)}
                            onSuccess={refreshDetail}
                        />
                        <RechargeMemberModal
                            visible={rechargeVisible}
                            memberId={member.id}
                            onCancel={() => setRechargeVisible(false)}
                            onSuccess={refreshDetail}
                        />
                        <MemberBalanceRefundModal
                            visible={refundVisible}
                            memberId={member.id}
                            onCancel={() => setRefundVisible(false)}
                            onSuccess={refreshDetail}
                        />
                    </>
                ) : (
                    <Empty description="暂无会员信息" />
                )}
            </Spin>
        </Expaned>
    )

    return props.drawer ? (
        <div className="member-detail-dialog-layer">
            <BaseDialog isShow title="会员详情" width={1000} onClose={closeDetail}>
                <Column className="member-detail member-detail--dialog">{detailBody}</Column>
            </BaseDialog>
        </div>
    ) : (
        <Column className="member-detail">
            <MemberPageHeader title="会员详情" onBack={closeDetail} backText="返回列表" />
            {detailBody}
        </Column>
    )
}

export default MemberDetail
